import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Mail, Phone, MapPin, Clock, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { countryCodes } from '../mock';
import { toast } from 'sonner';
import { validateEmail, validatePhoneNumber, formatPhoneNumber } from '../utils/validation';
import { useNavigate } from 'react-router-dom';
import { fetchGeolocation, submitLead, getCountryDialCode, getCountryFlag } from '../services/api';

export const Contact = () => {
  const navigate = useNavigate();
  const [geoData, setGeoData] = useState(null);
  const [isLoadingGeo, setIsLoadingGeo] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryDialCode: 1,
    phoneNumber: '',
    description: ''
  });
  
  // Validation errors state
  const [errors, setErrors] = useState({
    email: '',
    phoneNumber: ''
  });

  // Fetch geolocation on mount
  useEffect(() => {
    const loadGeo = async () => {
      setIsLoadingGeo(true);
      const result = await fetchGeolocation();
      if (result.success) {
        setGeoData(result);
        const dialCode = getCountryDialCode(result.data.countryCode);
        setFormData(prev => ({ ...prev, countryDialCode: dialCode }));
      }
      setIsLoadingGeo(false);
    };
    loadGeo();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isLoadingGeo) {
      toast.error('Please wait while we fetch your location...');
      return;
    }
    
    if (!geoData || !geoData.data) {
      toast.error('Unable to fetch location. Please refresh and try again.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const result = await submitLead({
        name: formData.name,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        description: formData.description,
        countryDialCode: formData.countryDialCode,
        country: geoData.data.countryCode || 'US'
      }, geoData);
      
      if (result.success) {
        console.log('✅ Form submission successful:', result);
        toast.success(result.message || 'Thank you! We will contact you soon.');
        
        // Use window.location.href instead of React navigate
        if (result.redirectUrl) {
          console.log('🔀 Redirecting to:', result.redirectUrl);
          window.location.href = result.redirectUrl;
        } else {
          console.warn('⚠️ No redirectUrl in result:', result);
        }
      } else {
        toast.error(result.error || 'Failed to submit form');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-20">
      <Helmet>
        <title>Contact Us | Trademark Infinite - Get Expert Trademark Help Today</title>
        <meta name="description" content="Contact Trademark Infinite for trademark registration assistance. Call +1 (888) 201-6668 or email info@trademarkinfinite.com for expert help." />
      </Helmet>
      
      {/* Hero Section with Modern Gradient */}
      <section className="relative bg-gradient-to-br from-orange-600 via-orange-500 to-orange-700 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1.5s' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-6xl font-bold mb-6">Contact Us</h1>
            <p className="text-2xl text-white/95">
              Get in touch with our team of trademark experts
            </p>
          </div>
        </div>
      </section>

      {/* Hero Image Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl h-96">
              {/* Background Image */}
              <img 
                src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1200&q=80" 
                alt="Customer support team" 
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/30 z-10"></div>
              
              <div className="absolute inset-0 flex items-center z-20">
                <div className="text-white p-12 max-w-2xl">
                  <div className="bg-orange-600 w-20 h-20 rounded-2xl flex items-center justify-center mb-6 shadow-xl">
                    <MessageSquare className="text-white" size={40} />
                  </div>
                  <h2 className="text-4xl font-bold mb-4 drop-shadow-2xl">We're Here to Help</h2>
                  <p className="text-xl text-white leading-relaxed drop-shadow-lg">Have questions? Need guidance? Our dedicated support team is available to assist you with all your trademark and intellectual property needs.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 max-w-7xl mx-auto">
            {/* Contact Information */}
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Get In Touch</h2>
              <p className="text-gray-600 mb-10 text-xl leading-relaxed">
                Have questions about trademark registration? Our team is here to help you protect your brand.
              </p>

              <div className="space-y-8">
                <div className="flex items-start gap-6 bg-white p-6 rounded-2xl shadow-lg">
                  <div className="bg-orange-100 w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="text-orange-600" size={28} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 text-xl">Email</h3>
                    <p className="text-gray-600 text-lg">info@trademarkinfinite.com</p>
                    <p className="text-sm text-gray-500 mt-1">We respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start gap-6 bg-white p-6 rounded-2xl shadow-lg">
                  <div className="bg-orange-100 w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="text-orange-600" size={28} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 text-xl">Phone</h3>
                    <p className="text-gray-600 text-lg">+1 (888) 201-6668</p>
                    <p className="text-sm text-gray-500 mt-1">Monday - Friday, 9AM - 6PM EST</p>
                  </div>
                </div>

                <div className="flex items-start gap-6 bg-white p-6 rounded-2xl shadow-lg">
                  <div className="bg-orange-100 w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-orange-600" size={28} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 text-xl">Office</h3>
                    <p className="text-gray-600 text-lg">One Wilshire Building<br />624 S Grand Ave Floor 21<br />Los Angeles, CA 90017</p>
                    <p className="text-sm text-gray-500 mt-1">Visit by appointment only</p>
                  </div>
                </div>

                <div className="flex items-start gap-6 bg-white p-6 rounded-2xl shadow-lg">
                  <div className="bg-orange-100 w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="text-orange-600" size={28} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 text-xl">Business Hours</h3>
                    <p className="text-gray-600">
                      Monday - Friday: 9:00 AM - 6:00 PM<br />
                      Saturday: 10:00 AM - 2:00 PM<br />
                      Sunday: Closed
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-3xl p-10 shadow-2xl border border-gray-100">
              <h3 className="text-3xl font-bold text-gray-900 mb-8">Send Us a Message</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                  <Input
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your full name"
                    className="h-12"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({ ...formData, email: value });
                      if (errors.email) {
                        setErrors(prev => ({ ...prev, email: '' }));
                      }
                    }}
                    onBlur={() => {
                      const validation = validateEmail(formData.email);
                      if (!validation.isValid) {
                        setErrors(prev => ({ ...prev, email: validation.error }));
                      }
                    }}
                    placeholder="your@email.com"
                    className={`h-12 ${errors.email ? 'border-red-500' : ''}`}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                  <div className="flex gap-3">
                    <Select
                      value={`+${formData.countryDialCode}`}
                      onValueChange={(value) => {
                        const dialCode = parseInt(value.replace('+', ''));
                        setFormData({ ...formData, countryDialCode: dialCode });
                      }}
                    >
                      <SelectTrigger className="w-32 h-12 text-gray-900">
                        <SelectValue>
                          {(() => {
                            const selected = countryCodes.find(c => c.code === `+${formData.countryDialCode}`);
                            return selected ? `${selected.flag} ${selected.code}` : '🇺🇸 +1';
                          })()}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {countryCodes.map((item) => (
                          <SelectItem key={item.country} value={item.code}>
                            <span className="flex items-center gap-2">
                              <span>{item.flag}</span>
                              <span>{item.code}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="tel"
                      required
                      value={formData.phoneNumber}
                      onChange={(e) => {
                        const formatted = formatPhoneNumber(e.target.value);
                        setFormData({ ...formData, phoneNumber: formatted });
                        if (errors.phoneNumber) {
                          setErrors(prev => ({ ...prev, phoneNumber: '' }));
                        }
                      }}
                      onBlur={() => {
                        const validation = validatePhoneNumber(formData.phoneNumber);
                        if (!validation.isValid) {
                          setErrors(prev => ({ ...prev, phoneNumber: validation.error }));
                        }
                      }}
                      placeholder="Phone number"
                      maxLength={10}
                      className={`flex-1 h-12 ${errors.phoneNumber ? 'border-red-500' : ''} text-gray-900`}
                    />
                  </div>
                  {errors.phoneNumber && (
                    <p className="text-red-500 text-xs mt-1">{errors.phoneNumber}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                  <Textarea
                    required
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Tell us about your needs..."
                    rows={6}
                    className="resize-none text-gray-900"
                  />
                </div>

                {/* {!isLoadingGeo && geoData && geoData.data && (
                  <div className="text-center">
                    <p className="text-sm text-gray-600">
                      {getCountryFlag(geoData.data.countryCode)} {geoData.data.countryName}
                    </p>
                  </div>
                )} */}

                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full bg-primary hover:bg-primary/90 h-14 text-lg disabled:opacity-50"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};